/**
 * THE BROWSER'S END OF THE TYPED CLIENT.
 *
 * `src/screens/client.ts` already owns the contract — which commands exist,
 * which of them need a retry key, what each view's query and result look like.
 * All this adds is the transport: `fetch`, with the session cookie, and a
 * `crypto.randomUUID` for the keys. Nothing here knows a business rule, and
 * nothing here invents a URL that the client does not already own.
 */
import { ApiClient, ApiError, type Transport, type CommandName, type CommandBodies, type ViewName, type Views } from '../../src/screens/client.js';

export { ApiError };
export type { CommandName, CommandBodies, ViewName, Views };

const browserTransport: Transport = {
  async send(req) {
    const res = await fetch(req.path, {
      method: req.method,
      headers: req.headers,
      // the session is a cookie the door set; it is never read by script
      credentials: 'same-origin',
      ...(req.body === undefined ? {} : { body: req.body }),
    });
    const body = await res.text();
    const headers: Record<string, string> = {};
    res.headers.forEach((v: string, k: string) => { headers[k.toLowerCase()] = v; });
    return { status: res.status, headers, body };
  },
};

export const api = new ApiClient({
  transport: browserTransport,
  newKey: () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `k-${Date.now()}-${Math.random().toString(36).slice(2)}`),
});

export interface Session {
  actorId: number;
  /**
   * 14 Oct — THE COMPANY WHOSE SCREEN THIS IS. The hostname already decided it
   * (ADR-010); this is the same answer in words the rail can draw, so the mark on
   * the page is the company's own and not the letters "CTS".
   */
  company?: { code: string; tradingName: string; markSvg: string | null; displayName: string };
  functions: readonly string[];
  /** 0173 — the business roles held (CEO, BRANCH_MANAGER, SALES_MANAGER, ACCOUNTANT, OPERATOR, …); empty for a user granted functions directly. */
  roles: readonly string[];
  /**
   * 0121 — WHICH YARD THIS SESSION IS WORKING AT, and the yards it may choose between.
   *
   * A04 made work carry a branch and made only its holders able to approve it. Nothing let
   * a person say which yard they were AT: the branch came from their standing default, so
   * somebody covering two yards recorded everything at one of them whatever building they
   * were standing in. `branches` lists only OPEN branches they hold; fewer than two means
   * there is nothing to choose and no picker is shown.
   */
  activeBranchId: number | null;
  branches: readonly { branchId: number; code: string; name: string; isDefault: boolean }[];
}

/** Does this user hold the function a screen or an action needs? */
export function can(session: Session | null, fn: string): boolean {
  if (!fn) return true;                       // a screen every signed-in user sees
  return !!session && session.functions.includes(fn);
}

/**
 * MAY THIS PERSON DECIDE SOMETHING THEY RAISED THEMSELVES? (0173, and the owner
 * walking into it on 12 October.)
 *
 * The server's rule is `segregated()`: the self-approval refusal binds anyone
 * holding a BUSINESS ROLE, except the CEO, and does not bind a user who holds
 * none — the developer, the migration actor, a one-person company before roles
 * exist. The screens were STRICTER than that: they hid Approve from the raiser
 * whatever their roles, so the owner raised an offer and was left with no way
 * to send it, told "somebody else decides it" when the server would have let
 * him. A screen that refuses what the server allows is a dead end.
 *
 * This is the same sentence as `src/platform/approvals.ts`, deliberately
 * duplicated rather than fetched: it decides only what to SHOW, and the server
 * decides again when the button is pressed.
 */
const BUSINESS_ROLES = ['CEO', 'BRANCH_MANAGER', 'SALES_MANAGER', 'ACCOUNTANT', 'OPERATOR', 'SUPERVISOR', 'OFFICE', 'YARD'];
export function mayDecideOwn(session: Session | null): boolean {
  if (!session) return false;
  const held = session.roles.filter((r) => BUSINESS_ROLES.includes(r));
  return held.length === 0 || held.includes('CEO');
}

/**
 * What to put in front of a person when a call fails.
 *
 * The screen's own contract (`SCREENS[].messages`) says what a refusal MEANS
 * on that screen; the door's message says what happened. Both are shown: the
 * first is the sentence, the second is the detail under it, because a message
 * that hides the server's own words makes an operator's phone call to us
 * useless.
 */
export function explain(e: unknown, messages: Record<string, string>): { title: string; detail?: string; reference?: string; code?: string; issues: { path: string; message: string }[] } {
  if (e instanceof ApiError) {
    const known = messages[e.code];
    return {
      code: e.code,
      title: known ?? e.message,
      ...(known && e.message && e.message !== known ? { detail: e.message } : {}),
      ...(e.reference ? { reference: e.reference } : {}),
      issues: e.issues.map((i) => ({ path: i.path, message: i.message })),
    };
  }
  // a dropped connection, a DNS failure, the browser offline: the operator
  // needs to know it did not reach us, which is a different fact from a refusal
  return { title: 'The system could not be reached. Check the connection and try again.', detail: e instanceof Error ? e.message : String(e), issues: [] };
}

/** The issue for one field, by the path the door used. */
export function issueFor(issues: { path: string; message: string }[], field: string): string | undefined {
  return issues.find((i) => i.path === field || i.path.endsWith(`.${field}`))?.message;
}
